/**
 * @file
 * Site script.
 */

// Global Variable: Store the hash.
// DON'T put this code inside the $() function, it has to be executed
// right away before the browser can start scrolling!
window.ahsPageLoadHash = ('hash' in window.location) ? window.location.hash : '';

// Delete hash so the page won't scroll to it.
if (window.ahsPageLoadHash) {
  window.history.replaceState({}, '', '');
}

(function siteScript($, Drupal, window, once) {
  'use strict';

  // Page load scroll.
  let mPageLoadAutoScrolled = false;

  // Sticky header outer height.
  let mStickyHeaderOuterHeight = null;

  /* Passive event detection */
  let passiveEventIsSupported = false;

  try {
    const testPassiveEventObject = Object.defineProperty({}, "passive", {
      get() {
        passiveEventIsSupported = true;
        return null;
      },
    });
    window.addEventListener("test", null, testPassiveEventObject);
    window.removeEventListener("test", null, testPassiveEventObject);
  } catch (err) {
    // Suppress.
    passiveEventIsSupported = false;
  }

  // Detect focus option.preventScroll.
  let focusPreventScrollIsSupported = false;
  try {
    let testFocusPreventScrollElement = document.createElement('div');
    testFocusPreventScrollElement.focus({ preventScroll: true });
    focusPreventScrollIsSupported = true;
    testFocusPreventScrollElement = null;
  } catch (e) {
    // Browser doesn't support preventScroll option.
    // Default behavior will scroll to element.
    focusPreventScrollIsSupported = false;
  }

  /**
   * Event options with "passive" set to true if the browser supports it,
   *
   * @return {Object|false}
   *   The event options with "passive" set to true OR
   *   false if the browser does not support passive.
   */
  function getPassiveEventOptions() {
    return passiveEventIsSupported ? { passive: true } : false;
  }

  /**
   * Debounce.
   *
   * Copied from docroot/core/misc/debounce.js to keep dependencies low for the
   * priority JS which is loaded with a weight so that it runs before all
   * others.
   */
  function debounce(func, wait, immediate) {
    let timeout;
    let result;
    return function debounceInner(...args) {
      const context = this;
      const later = function debounceInnerLater() {
        timeout = null;
        if (!immediate) {
          result = func.apply(context, args);
        }
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) {
        result = func.apply(context, args);
      }
      return result;
    };
  }

  // Determine if the header is sticky.
  function isHeaderSticky() {
    const header = document.querySelector('.site-header__wrapper');
    return header && header.classList.contains('is-pinned');
  }

  /**
   * Create a resize observer to get the header height.
   */
  const headerResizeObserver = typeof ResizeObserver !== 'undefined' ?
    // Set up header resize observer.
    new ResizeObserver(debounce((entries) => {
      entries.forEach((entry) => {
        mStickyHeaderOuterHeight = entry.contentRect.height;
      });
    }, 50)) :
    // ResizeObserver not available.
    {
      observe: () => {},
    };

  /**
   * Get the height of the sticky header.
   *
   * @param {Boolean} refresh
   *  TRUE to refresh the cached height.
   */
  function getStickyHeaderOuterHeight(refresh) {
    let outerHeight;
    refresh = typeof refresh !== 'undefined' && refresh !== null ? refresh : false;
    if (!refresh && mStickyHeaderOuterHeight !== null) {
      return mStickyHeaderOuterHeight;
    }

    outerHeight = 0;
    const stickyHeader = document.querySelector('.site-header__wrapper');
    if (stickyHeader) {
      const boundingRect = stickyHeader.getBoundingClientRect();
      outerHeight = boundingRect.height;

      // Adjust for negative tops.
      if (boundingRect.top < 0) {
        outerHeight += boundingRect.top;
      }
    }

    mStickyHeaderOuterHeight = outerHeight;
    return outerHeight;
  }

  /**
   * Focus while ensuring tabindex.
   *
   * @param {HTMLElement|jQuery} target
   *   The target jQuery element.
   * @param {String} message
   *   An aria-label message.
   * @param {Boolean} allowScroll
   *   TRUE to allow browser scrolling. Defaults to FALSE.
   *
   * @return {jQuery}
   *   The target jQuery element.
   */
  function focus(target, message, allowScroll = false) {
    // Only 1 can be focused.
    // Attempting to set focus to a hidden element causes an error in IE.
    const $focusTarget = $(target).filter(':visible').first();
    if ($focusTarget.length > 0) {
      // Ensure tabindex so that it can be focused.
      $focusTarget
        // Skip if already have a tabindex.
        .not('[tabindex]')
        // Exclude tabbable elements (links) so that they keep their browser
        // defined tab order.
        .not('a, input, button')
        // Add tabindex.
        .attr('tabindex', '-1');

      // Set message.
      if (typeof message !== 'undefined' && message) {
        $focusTarget.attr('aria-label', message);
      }

      // Focus on the element.
      const focusElement = $focusTarget.get(0);
      const focusOptions = {};
      if (!allowScroll && focusPreventScrollIsSupported) {
        // Trigger focus without browser scrolling.
        // https://html.spec.whatwg.org/multipage/interaction.html#dom-focusoptions-preventscroll
        // https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/focus
        focusOptions.preventScroll = true;
      }

      focusElement.focus(focusOptions);
    }

    return $focusTarget;
  }

  /**
   * Scroll and focus to the target element.
   *
   * This accounts for dynamic elements such as the sticky header and lazy
   * loaded images and videos.
   *
   * @param {HTMLElement|jQuery} target
   *   The target jQuery element that will be scrolled to. The target is also
   *   focused when option.focusElement is not defined and option.noFocus is
   *   FALSE or not defined.
   * @param {Object} options
   *   The animation options with the following additions:
   *   - topBuffer (Integer): The amount of pixels above the target element.
   *   - noFocus (Boolean): If TRUE, then the target element is not focused.
   *     Defaults to FALSE.
   *   - focusElement (HTMLElement|jQuery): Element to focus instead of target.
   * @param {Number} recursion
   *   Internal. The recursion counter used internally.
   * @param {Object} prevState
   *   Internal. The previous state used internally.
   *
   * @return {jQuery}
   *   The target jQuery element.
   */
  function scrollTop(target, options = {}, recursion = 0, prevState = {}) {
    let $target = $(target).first();
    let $widgetTarget;
    let state = {};
    let hasPrevState = false;
    const errorThreshold = 16;
    const recursionLimit = 3;
    const topBufferDefault = 48;
    let scrollOptions;
    let completeOptions;

    // Previous state.
    if (typeof prevState !== 'object') {
      // Initialize prev state.
      state.prevState = {};
    } else {
      // Propagate the state to this pass.
      state = prevState;
      state.prevState = $.extend({}, prevState);
      hasPrevState = Object.keys(state.prevState).length > 0;
    }

    // Options: initialize to an object if not defined.
    state.options = typeof options === 'object' ? options : {};

    // Exit if there is no target.
    if ($target.length === 0) {
      return $target;
    }

    // Init recursion counter.
    state.recursion = typeof recursion !== 'undefined' ? recursion : 0;

    // Exit if the target is hidden and this is the first pass.
    // The jQuery offset is used to get position data and it does not support
    // hidden elements.
    if ($target.is(':hidden')) {
      if (state.recursion === 0) {
        return $target;
      }

      state.completed = true;
    }

    // Determine action.
    if (state.completed || state.stop) {
      // Catch completed and stopped to pass the final complete below.
    } else if (state.recursion >= recursionLimit) {
      // Recursion limit hit.
      if ($target.length > 0) {
        // Run final complete if there is a target.
        state.completed = true;
      }
    } else {
      // Process this scroll.
      // Improved targeting for some widgets.
      // Global content has some large top padding so target the header.
      $widgetTarget = $target.find('.global-content-widget__header');
      if ($widgetTarget.length > 0) {
        $target = $widgetTarget;
      }

      // Get offsets.
      state.winScrollTop = $(window).scrollTop() || 0;
      state.targetTop = $target.offset().top || 0;
      const stickyHeaderHeight = getStickyHeaderOuterHeight();

      // Buffers.
      if (!('topBuffer' in state.options) || typeof state.options.topBuffer === 'undefined') {
        state.options.topBuffer = topBufferDefault;
      }

      // Calculate the target scroll.
      state.targetScrollTop = Math.max(0, state.targetTop - state.options.topBuffer);
      // Assume sticky will be active.
      state.targetScrollTop = Math.max(0, state.targetScrollTop - stickyHeaderHeight);

      // Get header stats.
      state.isHeaderSticky = isHeaderSticky();

      // Scroll if:
      // - Scrolled below the target,
      // - OR further above the target than the threshold (buffer).
      state.scrollError = Math.floor(state.winScrollTop - state.targetScrollTop);

      // Bounce check caused by being on the threshold of sticky header enabled.
      state.bouncing = false;
      if (hasPrevState &&
          state.prevState.scrollError < 0 && state.scrollError > state.options.topBuffer &&
          !state.prevState.isHeaderSticky && state.isHeaderSticky) {
        state.bouncing = true;
        state.targetScrollTop = stickyHeaderHeight;
        state.options.once = true;
        state.options.topBuffer = 0;
        state.forced = true;
      }

      if (state.forced || Math.abs(state.scrollError) > errorThreshold) {
        // Scroll to the target.
        scrollOptions = $.extend({}, {
          duration: 500,
        }, state.options);

        // Override complete callback.
        // The options.complete is called after the target has been reached.
        if ('complete' in scrollOptions) {
          delete scrollOptions.complete;
        }

        // Scroll animation.
        $('html, body')
          .animate({ scrollTop: state.targetScrollTop }, scrollOptions)
          .promise().done(() => {
            // The .promise().done() fires after all elements have been animated.
            // This is needed so that:
            // - This is called only 1 time.
            // - The window.scrollTop is updated in the next call.
            // Re-scroll to adjust for dynamic elements such as:
            // - The sticky header enabling while scrolling.
            // - Lazy loading of images and videos that further push the
            //   target element down the page.
            let nextOptions;
            if (!('once' in state.options) || !state.options.once) {
              // Scroll again to adjust for dynamic elements.
              nextOptions = $.extend({}, state.options);

              // Scroll again or Final complete.
              window.setTimeout(() => {
                state.recursion++;
                scrollTop(target, nextOptions, state.recursion, state);
              }, 50);
            } else {
              state.completed = true;
            }
          });
      } else {
        // Already at or close to the target.
        state.completed = true;
      }
    }

    // Final Complete actions.
    if (state.completed && !state.completeProcessed) {
      state.completeProcessed = true;

      // Focus on the target.
      // If the code gets here, then it has scrolled to the target threshold.
      if (!state.options || !('noFocus' in state.options) || !state.options.noFocus) {
        // Focus without scrolling (if possible).
        if (state.options && ('focusElement' in state.options)) {
          focus(state.options.focusElement, '', false);
        } else {
          focus($target, '', false);
        }

        // Scroll again to account for browsers scrolling to focus the element.
        // The focus() is called to prevent scrolling but not all browsers
        // support it, example Safari. See focus() for details.
        completeOptions = $.extend({}, state.options, {
          noFocus: true,
          once: true,
        });
        window.setTimeout(() => {
          scrollTop(target, completeOptions, state.recursion, state);
        }, 50);
      } else if ('complete' in options) {
        // Call custom complete.
        state.options.complete.call($('html').get(0) || $([]), $target);
      }
    } else if (state.completed && state.options && ('complete' in state.options)) {
      // Call custom complete
      state.options.complete.call($('html').get(0) || $([]), $target);
    }

    return $target;
  }

  /**
   * Convert the input to a hash "#something".
   *
   * @param {String|Number} value
   *   The value to convert.
   *
   * @return {String}
   *   The value converted to a hash.
   *   Empty string is return when empty or not valid.
   */
  function convertToHash(value) {
    if (typeof value !== 'string' && typeof value !== 'number') {
      return '';
    }

    // Convert to a string.
    value += '';
    if (value.length === 0) {
      return '';
    }

    // Sanitize.
    if (typeof Drupal.ahsFrontendSecurity !== 'undefined' &&
        typeof Drupal.ahsFrontendSecurity.sanitize !== 'undefined') {
      value = Drupal.ahsFrontendSecurity.sanitize(value);
    }

    if (value.indexOf('#') !== 0) {
      // Prefix hash.
      return `#${value}`;
    }

    if (value.length === 1) {
      // Hash == "#".
      return '';
    }

    return value;
  }

  /**
   * Determine if this JS should process the anchor hash.
   *
   * @param {String} hash
   *   The anchor hash.
   */
  function hashIsExcluded(hash) {
    // Remove the '#' from the beginning.
    const hashClean = hash.replace(/^#+/g, '');
    // Map marker focus is handled by @organisms/facility-search-combo-block.js.
    if (/^location-\d+$/gi.test(hashClean)) {
      // Return a non-null value.
      return true;
    }
    // Physician Book Appt App is handled in Drupal library:
    // ahs_book_apt/widget.
    if (hashClean.toLowerCase() === 'physician-appointment') {
      // Return a non-null value.
      return true;
    }

    return false;
  }

  /**
   * Determine if can auto scroll to the hash on page load.
   *
   * @return Boolean
   *   TRUE if auto scroll is allowed.
   */
  function hasAutoScrollToHashOnLoad() {
    return window.ahsPageLoadHash &&
      !hashIsExcluded(window.ahsPageLoadHash);
  }

  /**
   * Scroll to an anchor.
   *
   * @param {String} hash
   *   The anchor hash: '#123' or '123'.
   * @param {Object} options
   *   See scrollTop(). Additional options:
   *   - 'setWindowHash' (Boolean): TRUE to set the window hash.
   *     Defaults to TRUE.
   *   - 'skipAfterScrollClick' (Boolean): TRUE to skip triggering any element
   *     click event after the scrolling is done.
   *
   * @return {jQuery}
   *   The anchor element if found.
   */
  function scrollToAnchorTarget(hash, options = {}) {
    const context = window.document;
    const id = convertToHash(hash);
    if (!id) {
      return $([]);
    }

    options = typeof options === 'object' ? options : {};
    const $element = $(id, context);
    if ($element.length === 0) {
      return $element;
    }

    let $scrollTarget = $element;
    let clickTarget = null;

    // Scroll and focus on the tab link instead of the tabpanel so that the
    // tab is in view and tab widgets can respond to the tab link focus.
    if (!('focusElement' in options) && $element.attr('role') === 'tabpanel') {
      const labeledById = $element.attr('aria-labelledby');
      let tabLabel = null;
      if (labeledById) {
        tabLabel = document.querySelector(`#${labeledById}`);
      }

      if (tabLabel) {
        $scrollTarget = $(tabLabel);
        // Set the click target to the tab label.
        // For some reason, calling .focus() on the tab link does not trigger
        // the focus event. The click event is also more widely used in the
        // tab widgets on the site.
        clickTarget = tabLabel;
      } else if (!('topBuffer' in options)) {
        // Assume a top buffer so that the tab link is in view.
        options.topBuffer = 100;
      }
    }

    if (!$scrollTarget.is(':visible')) {
      return $element;
    }

    // Remove the ID so the browser does not scroll to it.
    $element.data('ahsAnchorTargetOriginalId', $element.attr('id'));
    $element.removeAttr('id');

    // Save the original complete callback.
    if ('complete' in options) {
      options.scrollToAnchorTargetComplete = options.complete;
    }

    options.complete = () => {
      if ('scrollToAnchorTargetComplete' in options) {
        options.scrollToAnchorTargetComplete.call(this, $element);
      }

      const targetId = $element.data('ahsAnchorTargetOriginalId');
      if (typeof targetId !== 'undefined' && targetId) {
        // Set the window hash.
        if (!('setWindowHash' in options) || options.setWindowHash) {
          // Update the window state.
          window.history.replaceState({}, '', `#${targetId}`);
        }

        // Restore id after set of the window hash.
        $element.attr('id', targetId);
      }

      if (clickTarget && (!('skipAfterScrollClick' in options) || !options.skipAfterScrollClick)) {
        clickTarget.click();
      }
    };

    scrollTop($scrollTarget, options);

    return $element;
  }

  /**
   * Hide empty sidebars.
   *
   * @param {HTMLElement} element
   *   The sidebar element.
   * @param {string} sidebarName
   *   The base name of the sidebar. Examples: 'first'.
   * @param {boolean} force
   *   Re-check even if already processed.
   */
  function processEmptySidebar(element, sidebarName, force) {
    const $element = $(element);
    let $elementInner;
    let hasContent = null;

    if ($element.length > 0 && ($element.not('sidebar-empty--processed') || force === true)) {
      $element.addClass('sidebar-empty--processed');
      hasContent = $element.children().length !== 0;
      if (hasContent) {
        // Check if there are children of the inner element (if there is one).
        $elementInner = $element.children(`.page__sidebar-${sidebarName}--inner`);
        if ($elementInner.length > 0 &&
            $elementInner.children()
              .not('.block-facet-empty')
              .not('.hidden')
              .not('.visually-hidden')
              .not('.hidden--important').length === 0) {
          hasContent = false;
        }
      }

      // Set on the element so others can use this info after it has been
      // processs.
      $element.data('ahsSidebarIsEmpty', !hasContent);

      // Hide the sidebar content.
      if (!hasContent) {
        $element
          .addClass('hidden--important')
          .closest('.page').removeClass(`page--has-sidebar-${sidebarName}`);
      }
    }

    if (hasContent === null) {
      return $element.data('ahsSidebarIsEmpty') !== true;
    }

    return hasContent;
  }

  /**
   * Process all sidebars within the provided context.
   */
  function processEmptySidebars(context, force) {
    let processed = false;
    let $pageContainer = null;

    if (typeof context === 'undefined' || context === null) {
      context = window.document;
    }

    const $sidebarFirst = $('.page__sidebar-first', context).first();

    // Hide empty sidebars.
    if ($sidebarFirst.length > 0) {
      processed = true;
      processEmptySidebar($sidebarFirst, 'first', force);
    }

    // Remove Page-No-Container Class if No Sidebars.
    if (processed) {
      if ($sidebarFirst.data('ahsSidebarIsEmpty') === true) {
        $pageContainer = $sidebarFirst.closest('.page--no-container');
        if ($pageContainer.length > 0) {
          $pageContainer.removeClass('page--no-container');
        }
      }
    }
  }

  /**
   * Onload scroll actions run only once.
   *
   * @param {HTMLElement} context
   *   The context element.
   * @return {jQuery}
   *   The scroll target if one exists in this context. Any non-null value
   *   can be returned to defer actions to other JS code.
   *   Example: facility-search-combo-block.js.
   */
  function onloadAccessibilityActions(context) {
    let $scrollTarget = null;
    let queryString;
    let locMatches;
    let locId;
    let $locOptionSelected;
    let viewsResetActive = null;

    // Add localStorage items on a reset button click.
    $(once('ahsThemeViewsReset', '[id^="edit-reset-"]', context)).on('click', () => {
      window.localStorage.setItem('ahsViewsReset', 'true');
    });

    // Check for localStorage items and resolve.
    if (window.localStorage.getItem('ahsViewsReset') === 'true') {
      viewsResetActive = true;
      window.localStorage.removeItem('ahsViewsReset');
    }

    // Default scroll complete.
    let fnScrollComplete = ($target) => {
      // Focus on the element that was scrolled to.
      focus($target);
    };

    // Return function to allow early exits.
    const fnReturn = ($newScrollTarget) => {
      // Set new target.
      if (typeof $newScrollTarget !== 'undefined') {
        $scrollTarget = $newScrollTarget;
      }

      // Scroll to the target.
      if ($scrollTarget !== null && $scrollTarget.length > 0) {
        scrollTop($scrollTarget, {
          topBuffer: 24,
          noFocus: true,
          complete: fnScrollComplete,
        });
      }

      return $scrollTarget;
    };

    // Priority selectors: Highest priority first.
    const prioritySelectors = {
      // Messages are highest priority.
      '.messages-wrapper': fnScrollComplete,
      // Contact form submission confirmation message.
      '.confirmation-message__inner': fnScrollComplete,
    };

    // Find the highest priority element on the page.
    $.each(prioritySelectors, (prioritySelector, priorityScrollComplete) => {
      const priorityElements = once('ahs-theme--onload', prioritySelector, context);
      if (priorityElements.length) {
        $scrollTarget = $(priorityElements);
        fnScrollComplete = priorityScrollComplete;
        // Break jQuery.each() loop.
        return false;
      }
      // Continue jQuery.each() loop.
      return true;
    });

    // Exit if priority scroll is set.
    if ($scrollTarget !== null && $scrollTarget.length > 0) {
      return fnReturn();
    }

    // Alternate focus elements.
    // Query string based actions.
    const winLocation = window.location;

    // Allow other JS to handle inbound anchors.
    if (winLocation && 'hash' in winLocation && hashIsExcluded(winLocation.hash)) {
      // Return a non-null value.
      return true;
    }

    const haveSearchQuery = winLocation && ('search' in winLocation) && winLocation.search;
    if (viewsResetActive || haveSearchQuery) {
      // Remove "?" from the beginning.
      queryString = haveSearchQuery ? winLocation.search.substring(1) : '';

      // Facility location selector form inbound requests.
      locMatches = /location=(\d+)/i.exec(queryString);
      if (locMatches !== null && locMatches.length > 0) {
        locId = locMatches[1];
        // If there is a facility form and the location is the selected value,
        // then get the first() selector on the page.
        // The form builder will decide if this value should be selected.
        $locOptionSelected = $(`.ahs-facility-location-selector-form__select option:selected[value="${locId}"]`, context).first();
        if ($locOptionSelected.length > 0) {
          $scrollTarget = $locOptionSelected.closest('[class$="grid-right"]');
          if ($scrollTarget.length === 0) {
            $scrollTarget = $($locOptionSelected.get(0).form);
          }
          // Scroll after a delay to give the map some time to build.
          setTimeout(() => { fnReturn(); }, 300);

          // Return TRUE to defer others from acting.
          return true;
        }
      }

      // Onload scroll to the main page exposed form if a search was performed.
      const viewsExposedForms = once('ahs-theme--onload-search-form', 'main .views-exposed-form:not(.newsroom-search-results-form)', context);
      if (viewsExposedForms.length) {
        $(viewsExposedForms)
          .not('.search-block-form')
          .not('.hidden')
          .first()
          .get()
          .forEach((form) => {
            const $form = $(form);

            // Each form has a wrapper. See form--views-exposed-form.html.twig.
            $scrollTarget = $form.parent();

            // Paged results scroll to main view.
            if (/page=\d+/ig.test(queryString)) {
              const $results = $('main .view', context).first();
              if ($results.length > 0) {
                $scrollTarget = $results;
              }
            }

            // Post scroll actions.
            fnScrollComplete = () => {
              // If there is result count text
              // set focus to that text.
              // Otherwise set focus to the first text input so
              // that screen readers announce the searched value.
              let $textInputs;
              let $textInputsFilled;
              let $textInputFormItem;
              const $resultText = $('.result-count', context);
              if ($resultText.length > 0) {
                // Have result summary: Focus summary.
                focus($resultText);
              } else {
                // No results summary: focus first empty input.
                $textInputs = $('input[type="text"]', $form);
                $textInputsFilled = $textInputs.filter(() => this.value.length > 0);
                if ($textInputsFilled.length > 0) {
                  // Set focus to first non-empty input to announce search terms.
                  $textInputFormItem = $textInputsFilled.first().not('.hidden').closest('.form-item');
                  if ($textInputFormItem.length > 0) {
                    // Focus on the form item so that the autocomplete suggestions
                    // dropdown does not open with suggestions for the already
                    // entered terms.
                    focus($textInputFormItem);
                  } else {
                    focus($textInputsFilled.first());
                  }
                } else {
                  // Set focus to first input so that they can keep searching.
                  focus($textInputs.first());
                }
              }
            };
          });
      }
    }

    return fnReturn();
  }

  /**
   * Actions taken on page load.
   */
  function onPageLoad(context) {
    let skipAccessibility = false;
    let skipAjaxNewContent = false;
    let accessibilityReturn = null;

    const headers = once('ahsHeaderResizeObserver', '.site-header__wrapper', context);
    if (headers.length > 0) {
      headerResizeObserver.observe(headers[0]);
    }

    processEmptySidebars(context);

    // On load page scroll happened.
    if (hasAutoScrollToHashOnLoad()) {
      skipAccessibility = true;
      skipAjaxNewContent = true;
    }

    // Accessibility actions - focus and scroll.
    if (!skipAccessibility) {
      accessibilityReturn = onloadAccessibilityActions(context);
      skipAjaxNewContent = accessibilityReturn === null;
    }

    if (!skipAjaxNewContent) {
      // On AJAX load, focus the loaded content container.
      // The container should have an aria-label attribute.
      const newContents = once('ahs-new-content-focus', '.new-content', context);
      if (newContents.length) {
        $(newContents).last().get().forEach((newContent) => {
          // Focus the element.
          focus($(newContent));
        });
      }
    }
  }

  /**
   * Create a fullscreen modal loading spinner.
   *
   * @param {String} className
   *   The class name for the overlay.
   * @param {String} message
   *   Optional. A message to show.
   *
   * @return {HTMLElement}
   *   The overlay element.
   */
  function createModalLoadingOverlay(className, message = '') {
    let messageElement = null;
    let modal = window.document.querySelector(`.${className}`);
    if (!modal) {
      modal = window.document.createElement('div');
      modal.classList.add('hidden');
      modal.classList.add(className);
      // Frontend class.
      modal.classList.add('loading-spinner--full-modal');
      // Accessibility.
      modal.setAttribute('role', 'alert');

      // Overlay.
      const overlayElement = window.document.createElement('div');
      overlayElement.classList.add('loading-spinner__overlay');
      modal.appendChild(overlayElement);

      // Spinner.
      const spinnerElement = window.document.createElement('div');
      spinnerElement.classList.add('loading-spinner__spinner');
      modal.appendChild(spinnerElement);

      // Message.
      messageElement = window.document.createElement('div');
      messageElement.classList.add('loading-spinner__message');
      modal.appendChild(messageElement);

      window.document.body.appendChild(modal);
    }

    if (message) {
      if (!messageElement) {
        messageElement = modal.querySelector('.loading-spinner__message');
      }

      messageElement.textContent = message;
    }

    return modal;
  }

  /**
   * Show a fullscreen modal loading spinner.
   *
   * @param {String} className
   *   The class name for the overlay.
   * @param {String} message
   *   Optional. A message to show.
   */
  function showModalLoadingOverlay(className, message = '') {
    const overlay = createModalLoadingOverlay(className, message);

    overlay.classList.remove('hidden');
    if (message) {
      const messageElement = overlay.querySelector('.loading-spinner__message');
      if (messageElement) {
        focus(messageElement);
      }
    }
  }

  /**
   * Hide the modal loading overlay.
   *
   * @param {String} className
   *   The class name for the overlay.
   */
  function hideModalLoadingOverlay(className) {
    const overlay = window.document.querySelector(`.${className}`);
    if (overlay) {
      overlay.classList.add('hidden');
    }
  }

  // Export utility functions for others to use.
  // Theme / PL JS should use Drupal.ahsThemeSite.
  // Drupal modules should use Drupal.ahsSite which is a wrapper library
  // for the Drupal.ahsThemeSite functions.
  Drupal.ahsThemeSite = {
    debounce: debounce,
    getPassiveEventOptions: getPassiveEventOptions,
    isHeaderSticky: isHeaderSticky,
    getStickyHeaderOuterHeight: getStickyHeaderOuterHeight,
    focus: focus,
    scrollTop: scrollTop,
    scrollToAnchorTarget: scrollToAnchorTarget,
    processEmptySidebars: processEmptySidebars,
    createModalLoadingOverlay: createModalLoadingOverlay,
    showModalLoadingOverlay: showModalLoadingOverlay,
    hideModalLoadingOverlay: hideModalLoadingOverlay,
    convertToHash: convertToHash,
  };

  // Window load listener.
  // The Window load event fires after DOMContentLoaded and
  // Drupal.attachBehaviors.
  $(window).on('load', () => {
    if (!mPageLoadAutoScrolled && hasAutoScrollToHashOnLoad()) {
      // Process only once to avoid re-scrolling on every
      // Drupal.attachBehaviors() call.
      mPageLoadAutoScrolled = true;
      window.setTimeout(() => {
        scrollToAnchorTarget(window.ahsPageLoadHash);
      }, 100);
    }
  });

  // Drupal Behaviors.
  Drupal.behaviors.themeSite = {
    attach(context) {
      if (context === window.document) {
        // Page loaded actions.
        if (once('themeSiteOnPageLoad', 'html').length) {
          onPageLoad(context);
        }
      } else if (context instanceof Element) {
        if (context.classList.contains('webform--form-wrapper')) {
          // Webform form AJAX response with messages - on error or success.
          if (once('themeSiteAccessibilityActions', context).length &&
              $(context).find('.messages-wrapper').length > 0) {
            onloadAccessibilityActions(context);
          }
        } else if (context.classList.contains('webform--confirmation-wrapper')) {
          // Webform confirmation AJAX response with messages.
          if (once('themeSiteAccessibilityActions', context).length &&
              $(context).find('.confirmation-message__inner').length > 0) {
            onloadAccessibilityActions(context);
          }
        }
      }
    },
  };
}(jQuery, Drupal, window, once));
